/* eslint-disable complexity */
/* eslint-disable no-nested-ternary */

'use client'

import { useState } from 'react'
import { Box, Link, Stack, Typography } from '@mui/material'
import { Icon, Image } from '@media-components'
import { LinkClick, type CardData } from '@components/common'
import { BaselineStyleClasses, CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StylesClassNames } from './styles'

export type FeatureCardData = Partial<CardData>

export type FeatureCardProps = {
  csx?: CustomStyles<StylesClassNames>
  translate?: number
  stopAnimation?: boolean
  hideIcon?: boolean
  withoutImageWrapper?: boolean
} & FeatureCardData

export function FeatureCard({
  csx,
  title,
  description,
  imageData,
  actionButtonData,
  translate = 30,
  stopAnimation = false,
  hideIcon = false,
  withoutImageWrapper = false,
}: FeatureCardProps) {
  const styles = getStyles(defaultStyles, csx)
  const [hover, setHover] = useState(false)

  return (
    <Stack
      spacing={2}
      {...styles('root')}
      {...(!stopAnimation && {
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(false),
      })}
      component={actionButtonData?.link ? Link : 'div'}
      {...(actionButtonData?.link && {
        href: actionButtonData.link,
        'aria-label': `${title} - ${description || ''}`,
        target: actionButtonData.newTab ? '_blank' : undefined,
      })}
      className={BaselineStyleClasses.imageTransitioningWrapper}
    >
      {imageData &&
        (withoutImageWrapper ? (
          <Image
            src={imageData.src}
            alt={imageData.alt}
            className={BaselineStyleClasses.imageTransition}
            {...styles('image', {
              transform: stopAnimation
                ? 'unset'
                : hover
                  ? 'translateY(0)'
                  : `translateY(${translate}px)`,
            })}
          />
        ) : (
          <Box {...styles('imageWrapper')}>
            <Image
              src={imageData.src}
              alt={imageData.alt}
              {...styles('image', {
                transform: stopAnimation
                  ? 'unset'
                  : hover
                    ? 'translateY(0)'
                    : `translateY(${translate}px)`,
              })}
            />
          </Box>
        ))}
      {!hideIcon && (
        <Box {...styles('iconWrapper')}>
          <Icon iconName="FlashCircle" />
        </Box>
      )}

      <Stack gap={1.5}>
        <Stack {...styles('textSection')} spacing={1}>
          {title && (
            <Typography
              variant="body1.600"
              color="text.primary"
              {...styles('heading')}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography
              variant="body3.400"
              color="text.secondary"
              {...styles('description')}
            >
              {description}
            </Typography>
          )}
        </Stack>
      </Stack>
      {actionButtonData && (
        <LinkClick link={undefined} {...styles('action')}>
          <Typography variant="body3.600">
            {actionButtonData.children}
          </Typography>
          <Icon
            iconName="ArrowRight"
            colorValues="text.primary"
            fontSize="extraSmall"
          />
        </LinkClick>
      )}
    </Stack>
  )
}

export default FeatureCard
